import React, { useState, useEffect } from 'react'
import { Box, Avatar, Typography, Tabs, Tab, Badge, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import PersonIcon from '@mui/icons-material/Person'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { collection, query, orderBy, where } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import SubscriptionTab from './SubscriptionTab'
import MessagesTab from './MessagesTab'
import MetaTraderAccountsTab from './MetaTraderAccountsTab'
import HelpTab from './HelpTab'
import WebinarTab from './WebinarTab'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  }
}

const UserProfile: React.FC = () => {
  const theme = useTheme()
  const { usermeta } = useApplicationSuiteContext()
  const [tabValue, setTabValue] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [newMessagesCount, setNewMessagesCount] = useState(0)
  const itemsPerPage = 10

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  // Query for new messages badge (only emailsInvited)
  const [newMessages] = useCollectionData(
    query(
      collection(firebase_firestore, `shareables`),
      where('emailsInvited', 'array-contains', usermeta.email),
      orderBy('createdAt', 'desc')
    )
  )

  // Update new messages count for badge
  useEffect(() => {
    setNewMessagesCount(newMessages?.length || 0)
  }, [newMessages])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    setCurrentPage(1) // Reset pagination when switching tabs
  }

  // Format simple date for messages
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
  }

  return (
    <Box
      sx={{
        maxWidth: '800px',
        width: '100%',
        margin: '0 auto',
        flexGrow: 1,
        height: '100vh',
        overflowY: 'auto',
        paddingBottom: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: isMobile ? 60 : 80,
            height: isMobile ? 60 : 80,
            margin: '0 auto 16px',
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <PersonIcon sx={{ fontSize: isMobile ? 30 : 40 }} />
        </Avatar>
        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom color="text.primary">
          My Account
        </Typography>
        <Typography variant={isMobile ? 'body2' : 'subtitle1'} color="text.secondary">
          Account and Strategy Suite information
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="profile tabs"
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontSize: isSmall ? '0.75rem' : '0.875rem',
              minWidth: isSmall ? 'auto' : 160,
              padding: isSmall ? '6px 8px' : '12px 16px',
            },
          }}
          centered
        >
          <Tab label={'Strategy Suite'} {...a11yProps(0)} />
          <Tab
            label={
              newMessagesCount > 0 ? (
                <Badge variant="dot" badgeContent=" " color="error">
                  Messages
                </Badge>
              ) : (
                'Messages'
              )
            }
            {...a11yProps(1)}
          />
          <Tab label="Webinars" {...a11yProps(2)} />
          <Tab label={isSmall ? 'MT Accounts' : 'MetaTrader Accounts'} {...a11yProps(3)} />
          <Tab label="Support" {...a11yProps(4)} />
        </Tabs>
      </Box>

      {/* Subscription Tab */}
      <TabPanel value={tabValue} index={0}>
        <SubscriptionTab usermeta={usermeta} />
      </TabPanel>

      {/* Messages Tab */}
      <TabPanel value={tabValue} index={1}>
        <MessagesTab
          usermeta={usermeta}
          formatDate={formatDate}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          setTotalPages={setTotalPages}
          itemsPerPage={itemsPerPage}
        />
      </TabPanel>

      {/* Webinars Tab */}
      <TabPanel value={tabValue} index={2}>
        <WebinarTab />
      </TabPanel>

      {/* MetaTrader Accounts Tab */}
      <TabPanel value={tabValue} index={3}>
        <MetaTraderAccountsTab accounts={usermeta.metaapi?.accounts || []} />
      </TabPanel>

      {/* Help Tab */}
      <TabPanel value={tabValue} index={4}>
        <HelpTab />
      </TabPanel>
    </Box>
  )
}

export default UserProfile
